import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { SECTION_TYPES } from '../CustomSection';

const F = ({ label, value, onChange, area, rows = 3 }) => (
  <div>
    <label className="text-xs font-medium text-stone-500 mb-1 block">{label}</label>
    {area
      ? <Textarea rows={rows} value={value ?? ''} onChange={e => onChange(e.target.value)} />
      : <Input value={value ?? ''} onChange={e => onChange(e.target.value)} />}
  </div>
);

const Select = ({ label, value, onChange, options }) => (
  <div>
    <label className="text-xs font-medium text-stone-500 mb-1 block">{label}</label>
    <select value={value} onChange={e => onChange(e.target.value)} className="border border-stone-200 rounded-lg px-3 py-2 text-sm w-full bg-white">
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

export const sectionTypeLabel = (type) => SECTION_TYPES.find(t => t.type === type)?.label || type;

export const CustomSectionEditor = ({ data, type, onChange }) => {
  const set = (key, value) => onChange({ ...data, [key]: value });
  const setListItem = (listKey, i, patch) => {
    const list = [...(data[listKey] || [])];
    list[i] = { ...list[i], ...patch };
    set(listKey, list);
  };

  switch (type) {
    case 'text':
      return (
        <div className="space-y-3">
          <F label="Kicker (small gold label)" value={data.kicker} onChange={v => set('kicker', v)} />
          <F label="Heading" value={data.heading} onChange={v => set('heading', v)} />
          <F label="Body text" value={data.body} onChange={v => set('body', v)} area rows={4} />
          <Select label="Alignment" value={data.align || 'left'} onChange={v => set('align', v)}
            options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }]} />
        </div>
      );
    case 'text_image':
      return (
        <div className="space-y-3">
          <F label="Kicker" value={data.kicker} onChange={v => set('kicker', v)} />
          <F label="Heading" value={data.heading} onChange={v => set('heading', v)} />
          <F label="Body text" value={data.body} onChange={v => set('body', v)} area rows={4} />
          <F label="Image URL" value={data.image} onChange={v => set('image', v)} />
          <div className="grid grid-cols-3 gap-3">
            <Select label="Image side" value={data.image_side || 'right'} onChange={v => set('image_side', v)}
              options={[{ value: 'right', label: 'Right' }, { value: 'left', label: 'Left' }]} />
            <F label="CTA label (optional)" value={data.cta_label} onChange={v => set('cta_label', v)} />
            <F label="CTA URL" value={data.cta_url} onChange={v => set('cta_url', v)} />
          </div>
        </div>
      );
    case 'image_banner':
      return (
        <div className="space-y-3">
          <F label="Image URL" value={data.image} onChange={v => set('image', v)} />
          <F label="Heading (overlay, optional)" value={data.heading} onChange={v => set('heading', v)} />
          <F label="Caption (optional)" value={data.caption} onChange={v => set('caption', v)} />
        </div>
      );
    case 'gallery_album':
      return (
        <div className="space-y-3">
          <F label="Kicker" value={data.kicker} onChange={v => set('kicker', v)} />
          <F label="Heading" value={data.heading} onChange={v => set('heading', v)} />
          <F label="Note (optional)" value={data.note} onChange={v => set('note', v)} />
          {(data.chapters || []).map((c, i) => (
            <div key={i} className="border border-stone-200 rounded-lg p-3 space-y-2 relative">
              <button onClick={() => set('chapters', data.chapters.filter((_, j) => j !== i))} className="absolute top-2 right-2"><Trash2 className="w-4 h-4 text-red-400" /></button>
              <F label="Album name" value={c.name} onChange={v => setListItem('chapters', i, { name: v })} />
              <F label="Photo URLs (one per line)" value={(c.photos || []).join('\n')} onChange={v => setListItem('chapters', i, { photos: v.split('\n').map(s => s.trim()).filter(Boolean) })} area />
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => set('chapters', [...(data.chapters || []), { name: '', photos: [] }])}><Plus className="w-4 h-4 mr-1" /> Add album</Button>
        </div>
      );
    case 'youtube_strip':
      return (
        <div className="space-y-3">
          <F label="Kicker" value={data.kicker} onChange={v => set('kicker', v)} />
          <F label="Heading" value={data.heading} onChange={v => set('heading', v)} />
          <F label="YouTube URLs or video IDs (one per line)" value={(data.videos || []).join('\n')} onChange={v => set('videos', v.split('\n').map(s => s.trim()).filter(Boolean))} area rows={4} />
        </div>
      );
    case 'cta_banner':
      return (
        <div className="space-y-3">
          <F label="Heading" value={data.heading} onChange={v => set('heading', v)} />
          <F label="Copy" value={data.copy} onChange={v => set('copy', v)} area />
          <div className="grid grid-cols-3 gap-3">
            <F label="CTA label" value={data.cta_label} onChange={v => set('cta_label', v)} />
            <F label="CTA URL" value={data.cta_url} onChange={v => set('cta_url', v)} />
            <Select label="Style" value={data.style || 'green'} onChange={v => set('style', v)}
              options={[{ value: 'green', label: 'Deep Green' }, { value: 'light', label: 'Light' }]} />
          </div>
        </div>
      );
    case 'stats_band':
      return (
        <div className="space-y-2">
          {(data.stats || []).map((s, i) => (
            <div key={i} className="flex gap-2 items-center">
              <Input className="flex-1" placeholder="Value (e.g. 95)" value={s.value} onChange={e => setListItem('stats', i, { value: e.target.value })} />
              <Input className="flex-[2]" placeholder="Label" value={s.label} onChange={e => setListItem('stats', i, { label: e.target.value })} />
              <button onClick={() => set('stats', data.stats.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-red-400" /></button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => set('stats', [...(data.stats || []), { value: '', label: '' }])}><Plus className="w-4 h-4 mr-1" /> Add stat</Button>
        </div>
      );
    case 'faq':
      return (
        <div className="space-y-3">
          <F label="Heading" value={data.heading} onChange={v => set('heading', v)} />
          {(data.items || []).map((f, i) => (
            <div key={i} className="border border-stone-200 rounded-lg p-3 space-y-2 relative">
              <button onClick={() => set('items', data.items.filter((_, j) => j !== i))} className="absolute top-2 right-2"><Trash2 className="w-4 h-4 text-red-400" /></button>
              <F label="Question" value={f.q} onChange={v => setListItem('items', i, { q: v })} />
              <F label="Answer" value={f.a} onChange={v => setListItem('items', i, { a: v })} area />
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => set('items', [...(data.items || []), { q: '', a: '' }])}><Plus className="w-4 h-4 mr-1" /> Add FAQ</Button>
        </div>
      );
    default:
      return <p className="text-sm text-stone-500">Unknown section type</p>;
  }
};
